import React, { useEffect, useMemo, useState } from 'react';

import { format } from 'date-fns';
import QueryString from 'qs';
import { omit } from 'ramda';
import { useQuery, useQueryClient } from 'react-query';
import { useAsyncDebounce } from 'react-table';

import quickListing from 'api/quicklisting';
import { FETCH_QUICk_LISTING } from 'constants/queries';

import { QuickListingProps } from './QuickListing.props';
import QuickListingView from './QuickListing.view';

// Default table options (pagination)
const TABLE_OPTIONS = {
  page: 1,
  limit: 10,
};

const QucikListingContainer = ({
  location,
  history,
  ...props
}: QuickListingProps): JSX.Element => {
  // Query client for cache management
  const queryClient = useQueryClient();

  // State to manage table options, such as pagination and filter
  const [tableOptions, setTableOptions] =
    useState<Record<string, any>>(TABLE_OPTIONS);

  // Parse URL query string to extract filters and search parameters
  const queries = useMemo(
    () => QueryString.parse(new URLSearchParams(location.search).toString()),
    [location.search]
  );

  // Reset page to 1 whenever filters or search parameters change
  useEffect(() => {
    setTableOptions((prev) => ({ ...prev, page: 1 }));
  }, [queries.filter, queries.search]);

  // Fetch quick listing data from the API using react-query
  const { data, refetch, isFetching } = useQuery(
    FETCH_QUICk_LISTING,
    async () => {
      // Build query string with pagination and filters
      const queryStr = `${
        location.search ? location.search + '&' : '?'
      }${QueryString.stringify(tableOptions)}`;
      return quickListing.getQuickListingLogs(queryStr);
    }
  );

  // Debounced search handler for optimized filtering
  const onSearchKey = useAsyncDebounce(
    (searchKey: string) =>
      history.push({
        pathname: location.pathname,
        search:
          '?' +
          new URLSearchParams({
            ...omit(['search'], queries),
            ...(searchKey ? { search: searchKey } : {}),
          }).toString(),
      }),
    600
  );

  // Navigate to specific quick listing details page
  const setListing = (quickListing?: any) =>
    history.push({
      pathname: location.pathname,
      search:
        '?' +
        new URLSearchParams({
          ...omit(['_id'], queries),
          ...(quickListing ? { id: quickListing._id } : {}),
        }).toString(),
    });

  // Format API response data for rendering (convert timestamp to a readable format)
  const quickListingLog = useMemo(
    () =>
      (data?.data.logs || []).map((item: any) => ({
        ...item,
        createdAt: item.createdAt
          ? format(new Date(item.createdAt), 'MM/dd/y, hh:mm a')
          : '-', // Format date
      })),
    [data]
  );

  // Extract analytics data from the response
  const analytics = useMemo(() => data?.data.analytics || {}, [data]);

  // Extract pagination metadata from the response
  const meta = useMemo(
    () => data?.meta || { count: 0, limit: 10, page: 1 },
    [data]
  );

  // Set filters by updating the URL with the new filter query
  const onSetFilter = (value: string, key: string) =>
    history.push({
      pathname: location.pathname,
      search: '?' + new URLSearchParams({ [key]: value }).toString(),
    });

  // Clear all filters by resetting the URL query parameters
  const onClearFilter = () => history.push({ pathname: location.pathname });

  // Refetch data when query parameters or table options change
  useEffect(() => {
    queryClient.cancelQueries([FETCH_QUICk_LISTING]); // Cancel any ongoing fetch request
    setTimeout(refetch, 300); // Delay refetch to avoid rapid querying
  }, [location.search, tableOptions]);

  // Render the QuickListingView component with necessary props
  return (
    <QuickListingView
      setListing={setListing}
      data={quickListingLog || []}
      analytics={analytics}
      meta={meta}
      onSearchKey={onSearchKey}
      isLoading={isFetching}
      onClearFilter={onClearFilter}
      onSetFilter={onSetFilter}
      tableOptions={tableOptions}
      setTableOptions={setTableOptions}
    />
  );
};

export default QucikListingContainer;
